import { useState, useEffect } from "react";
import axios from "axios";
import { paged } from "./AdminPagedList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign, Ticket, Users, BarChart3, TrendingUp, CheckCircle, Clock, X,
  MoreHorizontal, Eye, Pencil, RotateCcw, Trash2, Save
} from "lucide-react";
import TicketCheckinModal from "./TicketCheckinModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: "Pendente", confirmed: "Confirmado", cancelled: "Cancelado",
  refunded: "Reembolsado", partially_refunded: "Reembolso Parcial",
};
const STATUS_COLORS = {
  confirmed: "text-green-400", refunded: "text-red-400", partially_refunded: "text-orange-400",
  pending: "text-yellow-400", cancelled: "text-zinc-400",
};
const TYPE_LABELS = { individual: "Individual", group_medium: "Grupo Médio", group_large: "Grupo Grande" };

export default function EventSalesDashboard({ eventId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Action states
  const [actionMenu, setActionMenu] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundType, setRefundType] = useState("full");
  const [refundAmount, setRefundAmount] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const [checkinBookingId, setCheckinBookingId] = useState(null);

  const fetchData = () => {
    if (!eventId) return;
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    axios.get(`${API}/bookings/event-dashboard/${eventId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setData(res.data)).catch(err => console.error(err)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && eventId) fetchData();
    // eslint-disable-next-line
  }, [open, eventId]);

  const fmt = (v) => `R$ ${(v || 0).toFixed(2)}`;
  const canFinanciallyRefund = (booking) => (
    booking?.status === "confirmed"
    || (booking?.status === "cancelled" && booking?.refund_reason === "event_cancelled_credit_conversion")
  );
  const maxRevenue = data?.sales_by_day ? Math.max(...data.sales_by_day.map(d => d.revenue), 1) : 1;

  const handleSaveEdit = async () => {
    if (!editOrder) return;
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(`${API}/bookings/${editOrder.id}`, {
        name: editOrder.name, email: editOrder.email,
        phone: editOrder.phone, status: editOrder.status,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Pedido atualizado");
      setEditOrder(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    }
  };

  const handleRefund = async () => {
    if (!refundOrder) return;
    try {
      const token = localStorage.getItem("auth_token");
      const body = refundType === "full" ? {} : { amount: parseFloat(refundAmount) };
      const res = await axios.post(`${API}/bookings/${refundOrder.id}/refund`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Reembolso de R$ ${res.data.amount_refunded?.toFixed(2)} processado`);
      if ((res.data.event_cancellation_credits_unrecovered || 0) > 0) {
        toast.warning(`${res.data.event_cancellation_credits_unrecovered} crédito(s) compensatório(s) já haviam sido usados e não puderam ser retirados.`);
      }
      setRefundOrder(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reembolsar");
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const token = localStorage.getItem("auth_token");
      await axios.delete(`${API}/bookings/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Pedido excluído");
      setDeleteConfirm(null);
      setDeleteStep(0);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handleCheckin = (order) => {
    setCheckinBookingId(order.id);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#fccf1f] text-lg">Vendas — {data?.event?.title}</DialogTitle>
            <DialogDescription className="text-zinc-400">{data?.event?.date ? `${data.event.date.slice(8,10)}-${data.event.date.slice(5,7)}-${data.event.date.slice(0,4)}` : ''}</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center p-12"><Clock className="w-8 h-8 animate-pulse text-zinc-400" /></div>
          ) : data ? (
            <div className="space-y-5">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase font-medium mb-1"><DollarSign className="w-3.5 h-3.5 text-green-400" />Receita Líquida</div>
                    <p className="text-xl font-bold text-green-400">{fmt(data.revenue.net)}</p>
                    <p className="text-[10px] text-zinc-500">Ingressos: {fmt(data.revenue.base_price)} · Taxas: {fmt(data.revenue.fees)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase font-medium mb-1"><Ticket className="w-3.5 h-3.5 text-[#fccf1f]" />Ingressos Vendidos</div>
                    <p className="text-xl font-bold text-white">{data.tickets.sold}</p>
                    <p className="text-[10px] text-zinc-500">de {data.tickets.capacity} ({data.tickets.available} restantes)</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase font-medium mb-1"><Ticket className="w-3.5 h-3.5 text-green-400" />Créditos Usados</div>
                    <p className="text-xl font-bold text-green-400">{data.tickets.credits_used || data.revenue.credits_used || 0}</p>
                    <p className="text-[10px] text-zinc-500">cortesias aplicadas</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase font-medium mb-1"><CheckCircle className="w-3.5 h-3.5 text-blue-400" />Check-in</div>
                    <p className="text-xl font-bold text-blue-400">{data.tickets.checkin_rate}%</p>
                    <p className="text-[10px] text-zinc-500">{data.tickets.checked_in} de {data.tickets.sold} ingressos</p>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardContent className="pt-4 pb-3 px-4">
                    <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase font-medium mb-1"><TrendingUp className="w-3.5 h-3.5 text-orange-400" />Reembolsos</div>
                    <p className="text-xl font-bold text-orange-400">{fmt(data.revenue.refunded)}</p>
                    <p className="text-[10px] text-zinc-500">{data.orders.filter(o => o.status === "refunded" || o.status === "partially_refunded").length} pedidos</p>
                  </CardContent>
                </Card>
              </div>

              {/* Sales by Day Chart */}
              {data.sales_by_day.length > 0 && (
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#fccf1f]" />Vendas por Dia</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-end gap-1 h-32">
                      {data.sales_by_day.map(d => (
                        <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full">
                          <span className="text-[9px] text-zinc-400">{fmt(d.revenue)}</span>
                          <div className="w-full bg-[#fccf1f]/80 rounded-t" style={{ height: `${Math.max((d.revenue / maxRevenue) * 100, 4)}%` }} />
                          <span className="text-[9px] text-zinc-500 truncate w-full text-center">{d.date.slice(5)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sales by Type */}
              {data.sales_by_type.length > 0 && (
                <Card className="bg-zinc-800/60 border-white/10">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-white">Vendas por Tipo de Ingresso</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.sales_by_type.map(t => (
                        <div key={t.type} className="flex items-center justify-between bg-zinc-700/30 rounded px-3 py-2">
                          <span className="text-sm text-zinc-300">{TYPE_LABELS[t.type] || t.type}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-zinc-400">{t.count} ingressos</span>
                            <span className="text-xs text-green-400">{t.credits_used || 0} créditos</span>
                            <span className="text-sm text-white font-medium">{fmt(t.revenue)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Orders List */}
              <Card className="bg-zinc-800/60 border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Users className="w-4 h-4 text-[#fccf1f]" />Pedidos ({data.orders.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <div className="grid grid-cols-[0.5fr_1fr_0.5fr_0.3fr_0.5fr_0.6fr_0.3fr] gap-2 px-2 py-1 border-b border-white/5 text-[10px] text-zinc-500 uppercase font-medium">
                      <span>Pedido</span><span>Nome</span><span>Tipo</span><span>Qtd</span><span>Valor</span><span>Data</span><span>Ações</span>
                    </div>
                    {paged(data.orders, o => (
                      <div key={o.id} className="relative grid grid-cols-[0.5fr_1fr_0.5fr_0.3fr_0.5fr_0.6fr_0.3fr] gap-2 px-2 py-1.5 hover:bg-zinc-700/30 rounded text-xs items-center" data-testid={`sales-order-row-${o.id}`}>
                        <span className="text-zinc-400 font-mono">#{o.id?.slice(0, 8)}</span>
                        <div>
                          <p className="text-white truncate">{o.name}</p>
                          <p className={`text-[10px] ${STATUS_COLORS[o.status] || "text-zinc-500"}`}>{STATUS_LABELS[o.status] || o.status}</p>
                          {o.payment_provider_fallback_reason === "asaas_minimum_charge" && (
                            <p className="text-[10px] font-medium text-sky-300">Asaas → InfinityPay</p>
                          )}
                        </div>
                        <span className="text-zinc-400">{TYPE_LABELS[o.ticket_type] || o.ticket_type}</span>
                        <span className="text-zinc-300">{o.quantity}</span>
                        <span className="text-white font-medium">{fmt(o.total)}</span>
                        <span className="text-zinc-500">{o.created_at ? new Date(o.created_at).toLocaleDateString("pt-BR") : "-"}</span>
                        <div className="relative">
                          <Button size="sm" variant="ghost" onClick={() => setActionMenu(actionMenu === o.id ? null : o.id)} className="h-6 w-6 p-0 text-zinc-400 hover:text-white" data-testid={`sales-order-actions-${o.id}`}>
                            <MoreHorizontal className="w-3.5 h-3.5" />
                          </Button>
                          {actionMenu === o.id && (
                            <div className="absolute right-0 top-7 z-50 bg-zinc-800 border border-white/10 rounded-lg shadow-xl py-1 min-w-[160px]" data-testid={`sales-order-menu-${o.id}`}>
                              <button onClick={() => { setViewOrder(o); setActionMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-700"><Eye className="w-3.5 h-3.5" /> Exibir</button>
                              <button onClick={() => { setEditOrder({...o}); setActionMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-700"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                              {o.status === "confirmed" && o.checkin_status !== "checked_in" && (
                                <button onClick={() => { handleCheckin(o); setActionMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-400 hover:bg-zinc-700" data-testid={`sales-order-checkin-${o.id}`}><CheckCircle className="w-3.5 h-3.5" /> Realizar Check-in</button>
                              )}
                              {canFinanciallyRefund(o) && (
                                <button onClick={() => { setRefundOrder(o); setRefundType("full"); setRefundAmount(""); setActionMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-orange-400 hover:bg-zinc-700"><RotateCcw className="w-3.5 h-3.5" /> Reembolsar</button>
                              )}
                              <button onClick={() => { setDeleteConfirm(o); setDeleteStep(1); setActionMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-zinc-700"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg z-[60]">
          <DialogHeader>
            <DialogTitle className="text-[#fccf1f]">Pedido #{viewOrder?.id?.slice(0, 8)}</DialogTitle>
            <DialogDescription className="text-zinc-400">Detalhes do pedido</DialogDescription>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-zinc-500">Cliente:</span><p className="text-white">{viewOrder.name}</p></div>
                <div><span className="text-zinc-500">Email:</span><p className="text-white">{viewOrder.email}</p></div>
                <div><span className="text-zinc-500">Status:</span><p><span className={`text-xs font-bold uppercase ${STATUS_COLORS[viewOrder.status]}`}>{STATUS_LABELS[viewOrder.status]}</span></p></div>
                <div><span className="text-zinc-500">Tipo:</span><p className="text-white">{TYPE_LABELS[viewOrder.ticket_type] || viewOrder.ticket_type}</p></div>
                <div><span className="text-zinc-500">Quantidade:</span><p className="text-white">{viewOrder.quantity}</p></div>
                <div><span className="text-zinc-500">Valor:</span><p className="text-white font-bold">{fmt(viewOrder.total)}</p></div>
                <div><span className="text-zinc-500">Data:</span><p className="text-white">{viewOrder.created_at ? new Date(viewOrder.created_at).toLocaleString("pt-BR") : "-"}</p></div>
                <div><span className="text-zinc-500">Check-in:</span><p className="text-white">{viewOrder.checkin_status}</p></div>
              </div>
              {viewOrder.payment_provider_fallback_reason === "asaas_minimum_charge" && (
                <div className="rounded-lg border border-sky-400/25 bg-sky-400/10 p-3 text-xs text-sky-100">
                  <p className="font-bold">Troca automática de gateway</p>
                  <p className="mt-1 text-sky-100/80">
                    Asaas → InfinityPay porque o valor final foi {fmt(viewOrder.payment_provider_fallback_amount ?? viewOrder.total)}, abaixo do mínimo de R$ 5,00.
                  </p>
                  <p className="mt-1 text-sky-100/60">
                    Registrada em {viewOrder.payment_provider_fallback_at ? new Date(viewOrder.payment_provider_fallback_at).toLocaleString("pt-BR") : "-"}.
                  </p>
                </div>
              )}
              {viewOrder.refund_amount > 0 && <p className="text-orange-400 text-xs">Reembolsado: {fmt(viewOrder.refund_amount)}</p>}
              {viewOrder.replacement_credits_granted > 0 && <p className="text-purple-300 text-xs">Créditos concedidos pelo cancelamento do evento: {viewOrder.replacement_credits_granted}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!editOrder} onOpenChange={() => setEditOrder(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md z-[60]">
          <DialogHeader>
            <DialogTitle className="text-[#fccf1f]">Editar Pedido</DialogTitle>
            <DialogDescription className="text-zinc-400">Altere os dados do pedido</DialogDescription>
          </DialogHeader>
          {editOrder && (
            <div className="space-y-3">
              <div><label className="text-xs text-zinc-400">Nome</label><Input value={editOrder.name} onChange={e => setEditOrder({...editOrder, name: e.target.value})} className="bg-zinc-800 border-white/10 text-white" /></div>
              <div><label className="text-xs text-zinc-400">Email</label><Input value={editOrder.email} onChange={e => setEditOrder({...editOrder, email: e.target.value})} className="bg-zinc-800 border-white/10 text-white" /></div>
              <div>
                <label className="text-xs text-zinc-400">Status</label>
                <select value={editOrder.status} onChange={e => setEditOrder({...editOrder, status: e.target.value})} className="w-full bg-zinc-800 border border-white/10 text-white rounded-md px-3 py-2 text-sm">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveEdit} className="bg-green-600 text-white"><Save className="w-4 h-4 mr-2" /> Salvar</Button>
                <Button variant="outline" onClick={() => setEditOrder(null)} className="border-zinc-600 text-zinc-400"><X className="w-4 h-4 mr-2" /> Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={!!refundOrder} onOpenChange={() => setRefundOrder(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md z-[60]">
          <DialogHeader>
            <DialogTitle className="text-orange-400">Reembolsar Pedido</DialogTitle>
            <DialogDescription className="text-zinc-400">O reembolso será processado pelo provedor de pagamento original.</DialogDescription>
          </DialogHeader>
          {refundOrder && (
            <div className="space-y-4">
              <div className="bg-zinc-800/60 rounded-lg p-3 text-sm">
                <p className="text-zinc-400">Pedido: <span className="text-white font-medium">#{refundOrder.id?.slice(0, 8)}</span></p>
                <p className="text-zinc-400">Cliente: <span className="text-white">{refundOrder.name}</span></p>
                <p className="text-zinc-400">Valor total: <span className="text-white font-bold">{fmt(refundOrder.total)}</span></p>
                {refundOrder.replacement_credits_granted > 0 && <p className="mt-2 text-purple-300">O sistema tentará retirar os {refundOrder.replacement_credits_granted} crédito(s) concedidos automaticamente pelo cancelamento do evento.</p>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setRefundType("full")} className={refundType === "full" ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-400"}>Integral</Button>
                <Button size="sm" onClick={() => setRefundType("partial")} className={refundType === "partial" ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-400"}>Parcial</Button>
              </div>
              {refundType === "partial" && (
                <div>
                  <label className="text-xs text-zinc-400">Valor do reembolso (R$)</label>
                  <Input type="number" step="0.01" min="0.01" max={refundOrder.total} value={refundAmount} onChange={e => setRefundAmount(e.target.value)} placeholder="0.00" className="bg-zinc-800 border-white/10 text-white" />
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleRefund} disabled={refundType === "partial" && (!refundAmount || parseFloat(refundAmount) <= 0)} className="bg-orange-600 text-white hover:bg-orange-500">
                  <RotateCcw className="w-4 h-4 mr-2" /> Confirmar Reembolso
                </Button>
                <Button variant="outline" onClick={() => setRefundOrder(null)} className="border-zinc-600 text-zinc-400">Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Double Confirmation */}
      <Dialog open={deleteStep > 0} onOpenChange={() => { setDeleteConfirm(null); setDeleteStep(0); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-sm z-[60]">
          <DialogHeader>
            <DialogTitle className="text-red-400">{deleteStep === 1 ? "Excluir Pedido" : "Tem certeza absoluta?"}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {deleteStep === 1
                ? "Esta ação irá remover o pedido e todos os ingressos associados."
                : "Esta ação é irreversível. O pedido será permanentemente excluído."}
            </DialogDescription>
          </DialogHeader>
          {deleteConfirm && (
            <div className="space-y-3">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm">
                <p className="text-zinc-300">Pedido: <span className="text-white font-medium">#{deleteConfirm.id?.slice(0, 8)}</span></p>
                <p className="text-zinc-300">Cliente: <span className="text-white">{deleteConfirm.name}</span></p>
              </div>
              <div className="flex gap-2">
                {deleteStep === 1 ? (
                  <Button onClick={() => setDeleteStep(2)} className="bg-red-600 text-white hover:bg-red-500">Sim, excluir</Button>
                ) : (
                  <Button onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-500">Confirmo a exclusão permanente</Button>
                )}
                <Button variant="outline" onClick={() => { setDeleteConfirm(null); setDeleteStep(0); }} className="border-zinc-600 text-zinc-400">Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ticket Check-in Modal */}
      <TicketCheckinModal
        open={!!checkinBookingId}
        onClose={() => setCheckinBookingId(null)}
        bookingId={checkinBookingId}
        onUpdate={fetchData}
      />
    </>
  );
}
